import { RumInitConfiguration } from '@datadog/browser-rum-slim';
import { Accept } from 'react-dropzone';
import { ToastPosition } from 'react-toastify';

import { ISiteSubNavLinkItem } from '../components/Navigation/SiteSubNavigation/helpers/siteSubNavLinks';
import { getMykissEnv } from '@lib/environment/getMykissEnv';
import { isBrowser } from '@lib/environment/isBrowser';

import { SERVER_ENV_VARS, UNIVERSAL_ENV_VARS } from './environmentVariables';
import { getDomain } from '@domain/routes/urls';

export const RECENT_CATCH_LIMIT_DAYS = 14;
export const MILLION_USERS = '20';
export const MILLION_CATCHES = '20';
export const THOUSAND_GEAR_REVIEWS = '500';
export const MILLION_PERSONAL_BESTS = '1.5';

export const PAGE_TITLE_SUFFIX = ' | Fishbrain';
export const DEFAULT_LOGGED_IN_ROUTE = '/';
export const MIN_PASSWORD_LENGTH = 8;

export const SERVER_REQUEST_HEADERS = { 'USER-AGENT': 'mykiss-web' };
export const FISH_RULES_HEADERS = {
  'x-client-id': SERVER_ENV_VARS.FISHRULES_CLIENT_ID,
  'x-api-key': SERVER_ENV_VARS.FISHRULES_API_KEY,
  ...(!isBrowser() && SERVER_REQUEST_HEADERS),
};
export const INCORRECT_EMAIL_TOKEN_MSG = 'Incorrect email verification token.';
export const RECAPTCHA_ENTERPRISE_SITE_KEY = UNIVERSAL_ENV_VARS.RECAPTCHA_ENTERPRISE_SITE_KEY;

// Extensions are populated so FileDropzone's "supported types" hint reads correctly —
// `getListOfSupportedTypes` derives the visible string from the values of the Accept record.
export const ACCEPTED_IMAGE_UPLOAD_TYPES: Accept = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};
export const ACCEPTED_VIDEO_UPLOAD_TYPES: Accept = {
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/webm': ['.webm'],
};
export const BLOG_POSTS_PER_PAGE = 12;

// Consent Agreement UUIDs
export const CURRENT_CONSENT_MARKETING_EMAIL_UUID = 'fishbrain-marketing-email-rev_1_0-en';
export const CURRENT_CONSENT_TERMS_UUID = 'fishbrain-terms-and-conditions-rev_2_0-en';
export const CURRENT_CONSENT_PRIVACY_UUID = 'fishbrain-privacy-policy-rev_2_0-en';

interface ToastConfig {
  position: ToastPosition;
  autoClose: number;
}

export const TOAST_CONFIG: ToastConfig = {
  position: 'bottom-center',
  autoClose: 8000,
};

export const CANONICAL_ROOT_URL = getDomain();
export const META_HOMEPAGE_TITLE = 'Fishing App and Fishing Tools';
export const META_HOMEPAGE_DESCRIPTION = `Join over ${MILLION_USERS} million anglers using Fishbrain. Fishbrain is the world's #1 fishing app for finding fishing spots, forecasts, socializing, logging catches & more. Fish smarter with Fishbrain.`;

export const DATADOG_RUM_CONFIG: RumInitConfiguration = {
  applicationId: 'b080e31a-eda5-41eb-86e9-c67f024fd393',
  clientToken: UNIVERSAL_ENV_VARS.CLIENT_DATADOG_TOKEN,
  env: getMykissEnv(),
  service: 'mykiss',
  trackViewsManually: true,
  allowedTracingUrls: [/https:\/\/.*\.fishbrain\.com/],
  sessionSampleRate: 1,
};

export const ACCEPTED_VIDEO_MIME_TYPES = ['video/mp4', 'video/webm'];

export const CATCH_MAP_WIDTH = 650;
export const CATCH_MAP_HEIGHT = 450;

export const NO_REGULATION_TITLE = 'No regulations for this area yet';
export const NO_REGULATION_DESCRIIPTION =
  'We are working on adding regulations to your area. Please contact your regulation provider and ask them to support Fishbrain.';
export const REGULATION_DISCLAIMER_TEXT =
  'Disclaimer: Always check local fishing regulations, water access rights and land ownership before fishing, regardless of any catches logged in that area by the Fishbrain community. Fishbrain has mapped millions of acres of government-owned land across the USA to help you identify potential fishing access, but you are responsible for ensuring compliance with all legal requirements.';

export const SIGNUP_METHODS = {
  facebook: 'facebook',
  google: 'google',
  apple: 'apple',
  manual: 'manual',
} as const;

export const FEATURE_PAGE_NAV_LINKS: ISiteSubNavLinkItem[] = [
  {
    id: 'features',
    title: 'Overview',
    path: '/features',
  },
  {
    id: 'regulations',
    title: 'Rules and Regulations',
    path: '/features/rules-and-regulations',
  },
  {
    id: 'fishingSpots',
    title: 'Fishing spots',
    path: '/features/fishing-spots',
  },
  {
    id: 'fishingIdentifier',
    title: 'Fish Identifier',
    path: '/features/fish-identifier',
  },
  {
    id: 'fishingForcast',
    title: 'Fishing Forecast',
    path: '/features/fishing-forecasts',
  },
  {
    id: 'depthMaps',
    title: 'Depth Maps',
    path: '/features/depth-maps',
  },
  {
    id: 'waypoints',
    title: 'Waypoints',
    path: '/features/waypoints',
  },
  {
    id: 'logbook',
    title: 'Logbook',
    path: '/features/logbook',
  },
  {
    id: 'topWaters',
    title: 'Top waters',
    path: '/features/top-waters',
  },
  {
    id: 'publicPrivateLand',
    title: 'Public & Private Land',
    path: '/features/public-private-land',
  },
  {
    id: 'biteGuide',
    title: 'BiteGuide',
    path: '/features/biteguide',
  },
];

export const DIRECTORY_PAGE_NAV_LINKS: (ISiteSubNavLinkItem & { footerTitle: string })[] = [
  {
    id: 'countries',
    title: 'Countries',
    footerTitle: 'All countries',
    path: '/directory/countries',
    subPages: ['/directory/countries'],
  },
  {
    id: 'regions',
    title: 'Regions',
    footerTitle: 'All regions',
    path: '/directory/regions',
    subPages: ['/directory/regions'],
  },
  {
    id: 'cities',
    title: 'Cities',
    footerTitle: 'All cities',
    path: '/directory/cities',
    subPages: ['/directory/cities'],
  },
  {
    id: 'species',
    title: 'Species',
    footerTitle: 'All species',
    path: '/directory/species',
    subPages: ['/directory/species'],
  },
  {
    id: 'fishing-waters',
    title: 'Fishing Waters',
    footerTitle: 'All fishing waters',
    path: '/directory/fishing-waters',
    subPages: ['/directory/fishing-waters'],
  },
];

export const DISCOVER_PAGES_NAV_LINKS: ISiteSubNavLinkItem[] = [
  {
    id: 'discover',
    title: 'Explore Fishing',
    path: '/discover',
  },
  {
    id: 'discoverSpecies',
    title: 'Explore Species',
    path: '/discover/species',
  },
  {
    id: 'discoverUS',
    title: 'Fishing in the US',
    path: '/countries/us-united-states',
    subPages: ['/countries/us-united-states/', '/cities/us'],
  },
  {
    id: 'popularWaters',
    title: 'Popular waters',
    path: '/fishing-waters/popular',
  },
];

export const BLOG_PAGE_NAV_LINKS: ISiteSubNavLinkItem[] = [
  {
    id: 'all',
    title: 'All posts',
    path: '/blog',
  },
  {
    id: 'species',
    title: 'Species',
    path: '/blog/species',
  },
  {
    id: 'category',
    title: 'Categories',
    path: '/blog/categories',
  },
  {
    id: 'contributors',
    title: 'Contributors',
    path: '/blog/authors',
  },
];

export const IOS_APP_RATING = 4.7;
export const IOS_ALL_RATING_COUNT = '37.6K';
export const AVERAGE_APP_RATING = 4.3;
export const ANDROID_RATING_COUNT = '93K+';

export const META_ADVERTISE_TITLE = 'Meet Fishbrain';
export const META_ADVERTISE_DESCRIPTION =
  'Dive into a world where millions of passionate anglers and outdoor enthusiasts connect, engage, and share experiences.';
export const ADVERTISE_EMAIL = 'advertise@fishbrain.com';

/** The id of the section holding the advertise page's contact sales form. */
export const ADVERTISE_CONTACT_SALES_ID = 'contact-sales';

/** Public HubSpot embed ids. Owned by the sales team, not secrets. */
export const HUBSPOT_PORTAL_ID = '43959555';
export const HUBSPOT_REGION = 'na1';
export const HUBSPOT_ADVERTISE_FORM_ID = '89535df0-ea6f-408a-9f73-3da82f474f63';

export const DISTANCE_UNITS = {
  KMS: 'kms',
  MILES: 'miles',
};

export const WEB_GL_DISABLED_WARNING_MESSAGE =
  'WebGL must be enabled in your browser in order to view the map.';
export const WEB_GL_DISABLED_ERROR_MESSAGE = 'Failed to initialize WebGL';

export const SENTRY_DSN =
  'https://9f2030639b244327808aa95c917e15ca@o4510677991292928.ingest.de.sentry.io/4511091777667155';
